<template>
  <div v-if="open" class="profile-form-modal">
    <div class="profile-form-modal__backdrop" @click="onClose"></div>
    <div class="profile-form-modal__content">
      <div class="profile-form-modal__header">
        <h3>Completa tu perfil para inscribirte</h3>
        <button type="button" class="profile-form-modal__close" @click="onClose" aria-label="Cerrar">✕</button>
      </div>
      <p class="profile-form-modal__sub">
        Necesitamos los siguientes datos obligatorios para continuar con tu inscripción.
      </p>
      <form class="profile-form-modal__form" @submit.prevent="onSubmit">
        <div class="form-row">
          <label class="field-label" :class="{ 'field-label--error': errors.dateBirth }">
            Fecha de nacimiento <span class="required">*</span>
          </label>
          <input
            v-model="form.dateBirth"
            type="date"
            class="field-input"
            :class="{ 'field-input--error': errors.dateBirth }"
            @blur="touch('dateBirth')"
          />
        </div>
        <div class="form-row">
          <label class="field-label" :class="{ 'field-label--error': errors.country }">
            País <span class="required">*</span>
          </label>
          <select
            v-model="form.country"
            class="field-input"
            :class="{ 'field-input--error': errors.country }"
            @blur="touch('country')"
          >
            <option value="" disabled>Selecciona tu país</option>
            <option v-for="c in countryOptions" :key="c.code" :value="c.name">
              {{ c.name }}
            </option>
          </select>
        </div>
        <div class="form-row">
          <label class="field-label" :class="{ 'field-label--error': errors.department }">
            Departamento / Estado / Provincia <span class="required">*</span>
          </label>
          <input
            v-model="form.department"
            type="text"
            class="field-input"
            :class="{ 'field-input--error': errors.department }"
            placeholder="Ej. Cundinamarca"
            @blur="touch('department')"
          />
        </div>
        <div class="form-row">
          <span class="field-label" :class="{ 'field-label--error': errors.gender }">
            Género <span class="required">*</span>
          </span>
          <div class="gender-options">
            <label class="gender-option">
              <input type="radio" v-model="form.gender" value="female" @change="touch('gender')" />
              Femenino
            </label>
            <label class="gender-option">
              <input type="radio" v-model="form.gender" value="male" @change="touch('gender')" />
              Masculino
            </label>
            <label class="gender-option">
              <input type="radio" v-model="form.gender" value="other" @change="touch('gender')" />
              Otro
            </label>
          </div>
        </div>
        <div class="form-row">
          <label class="field-label">Ciudad</label>
          <input
            v-model="form.city"
            type="text"
            class="field-input"
            placeholder="Ej. Bogotá"
          />
        </div>
        <div class="form-row">
          <label class="field-label">Institución educativa</label>
          <input
            v-model="form.institution"
            type="text"
            class="field-input"
            placeholder="Ej. Colegio Ejemplo"
          />
        </div>
        <div class="form-actions">
          <button type="button" class="mob-btn mob-cta-gold-outline" @click="onClose">Cancelar</button>
          <button type="submit" class="mob-btn mob-cta-gold" :disabled="saving">Listo</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { getApiUrl } from "@/Utils/api-url-helper";
import { fetchCurrentUser } from "./registrationFlow";
import { enrollInCourse, clearRegistrationIntent } from "./registrationFlow";
import { COUNTRIES_ES } from "@/constants/countries.es";

const REQUIRED_FIELDS = ["dateBirth", "country", "department", "gender"];
const OLIMPIADAS_COURSE_ID = 636;

export default {
  name: "OlimpiadasProfileFormModal",
  props: {
    open: Boolean,
  },
  emits: ["close", "success"],
  data() {
    return {
      form: {
        name: "",
        dateBirth: "",
        country: "",
        department: "",
        gender: "",
        city: "",
        institution: "",
      },
      userId: null,
      touched: {},
      submitAttempted: false,
      saving: false,
    };
  },
  computed: {
    countryOptions() {
      return COUNTRIES_ES;
    },
    errors() {
      const e = {};
      REQUIRED_FIELDS.forEach((f) => {
        const v = this.form[f];
        const isEmpty = v == null || String(v).trim() === "";
        const showError = this.submitAttempted || this.touched[f];
        e[f] = showError && isEmpty;
      });
      return e;
    },
  },
  watch: {
    open(val) {
      if (val) {
        this.loadUser();
        this.touched = {};
        this.submitAttempted = false;
      }
    },
  },
  methods: {
    touch(field) {
      this.touched[field] = true;
    },
    async loadUser() {
      try {
        const res = await fetchCurrentUser();
        const u = res.data;
        this.userId = u.id;
        this.form.name = u.name || "";
        this.form.dateBirth = u.dateBirth ? (typeof u.dateBirth === "string" ? u.dateBirth : u.dateBirth.substring(0, 10)) : "";
        this.form.country = u.country || "";
        this.form.department = u.department || "";
        this.form.gender = u.gender || "";
        this.form.city = u.city || "";
        this.form.institution = u.institution || "";
      } catch (err) {
        console.error(err);
        this.$toasted?.error?.("No se pudo cargar tu perfil.");
      }
    },
    validate() {
      let valid = true;
      REQUIRED_FIELDS.forEach((f) => {
        const v = this.form[f];
        if (v == null || String(v).trim() === "") valid = false;
      });
      return valid;
    },
    async onSubmit() {
      this.submitAttempted = true;
      if (!this.validate()) return;

      this.saving = true;
      const apiBase = getApiUrl(import.meta.env.VITE_API_BASE_URL);
      const token = localStorage.getItem("token");

      try {
        await axios.put(
          `${apiBase}/user/editOptions/${this.userId}`,
          {
            name: this.form.name,
            dateBirth: this.form.dateBirth,
            country: this.form.country,
            department: this.form.department,
            gender: this.form.gender,
            city: this.form.city || null,
            institution: this.form.institution || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        try {
          await enrollInCourse(OLIMPIADAS_COURSE_ID);
        } catch (enrollErr) {
          if (enrollErr?.response?.status !== 400) throw enrollErr;
        }

        clearRegistrationIntent();
        this.$emit("success");
      } catch (err) {
        this.$toasted?.error?.("Error al guardar. Intenta de nuevo.", { position: "top-center", duration: 4000 });
      } finally {
        this.saving = false;
      }
    },
    onClose() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.profile-form-modal {
  position: fixed;
  inset: 0;
  z-index: 55;
  display: grid;
  place-items: center;
  padding: 20px;
}

.profile-form-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 18, 28, 0.55);
}

.profile-form-modal__content {
  position: relative;
  z-index: 1;
  width: min(440px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 24px 48px rgba(23, 30, 52, 0.2);
}

.profile-form-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
}

.profile-form-modal__header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #121826;
  margin: 0;
}

.profile-form-modal__close {
  flex-shrink: 0;
  border: none;
  background: #f2f3f7;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  font-size: 16px;
  color: #5b6476;
}

.profile-form-modal__sub {
  font-size: 14px;
  color: #5b6476;
  margin: 0 0 20px 0;
}

.profile-form-modal__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 14px;
  font-weight: 600;
  color: #1d2433;
}

.field-label--error {
  color: #dc2626;
}

.required {
  color: #dc2626;
}

.field-input {
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  color: #1d2433;
}

.field-input:focus {
  outline: none;
  border-color: #656ec9;
  box-shadow: 0 0 0 2px rgba(101, 110, 201, 0.2);
}

.field-input--error {
  border-color: #dc2626;
  background: #fef2f2;
}

.field-input--error:focus {
  border-color: #dc2626;
}

.gender-options {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
}

.gender-option {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #1d2433;
  cursor: pointer;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

</style>
